/**
 * Provider definitions and pure form helpers for the API Keys settings page.
 *
 * Field kinds:
 * - `secret` / `secret-json`: write-only credentials. Never prefilled from the
 *   API (only masked identifiers are shown), sent only when non-empty, and
 *   cleared after a successful save.
 * - `credential`: a write-only identifier (Meta App ID, TikTok Client Key) that
 *   is entered in plain text but otherwise behaves like a secret.
 * - `select` / `text` / `range`: non-sensitive settings, prefilled from the
 *   API and always sent with the provider's save (matching the legacy payloads).
 */
import { Brain, Facebook, MapPin, Mic, Share2, Sparkles, Volume2 } from 'lucide-react';

const WRITE_ONLY_KINDS = new Set(['secret', 'secret-json', 'credential']);

export const isWriteOnlyField = (field) => WRITE_ONLY_KINDS.has(field.kind);

export const PROVIDERS = [
  {
    id: 'fal',
    name: 'fal.ai Image Generation',
    shortName: 'fal.ai',
    description: 'Generate scenario images with AI',
    icon: Sparkles,
    accent: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
    testSlug: 'fal',
    configuredKey: 'fal_configured',
    identifier: (settings) => settings?.fal_api_key_masked
      && `Saved key ${settings.fal_api_key_masked}`,
    fields: [
      { key: 'fal_api_key', label: 'API key', kind: 'secret', placeholder: 'Enter fal.ai API key' },
      {
        key: 'fal_default_model',
        label: 'Default model',
        kind: 'select',
        defaultValue: 'fal-ai/flux/schnell',
        options: [
          { value: 'fal-ai/flux/schnell', label: 'Flux Schnell (Fast)' },
          { value: 'fal-ai/flux/dev', label: 'Flux Dev (Higher Quality)' },
          { value: 'fal-ai/flux-pro', label: 'Flux Pro (Best Quality)' },
        ],
      },
      {
        key: 'fal_default_style',
        label: 'Default style prompt',
        kind: 'text',
        defaultValue: '',
        placeholder: 'e.g., warm illustration, child-friendly anime style',
        help: 'This style is appended to all image generation prompts.',
      },
    ],
    setup: {
      steps: [
        'Create a free account at fal.ai',
        'Go to Dashboard → Keys',
        'Click "Create Key" and copy the key',
      ],
      link: 'https://fal.ai/dashboard/keys',
      linkText: 'Open fal.ai Dashboard',
    },
  },
  {
    id: 'anthropic',
    name: 'Anthropic (Claude)',
    shortName: 'Anthropic',
    description: 'AI-powered image prompt generation',
    icon: Brain,
    accent: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
    testSlug: 'anthropic',
    configuredKey: 'anthropic_configured',
    identifier: (settings) => settings?.anthropic_api_key_masked
      && `Saved key ${settings.anthropic_api_key_masked}`,
    fields: [
      { key: 'anthropic_api_key', label: 'API key', kind: 'secret', placeholder: 'Enter Anthropic API key' },
      {
        key: 'anthropic_model',
        label: 'Default model',
        kind: 'select',
        defaultValue: 'claude-haiku-4-5',
        help: 'Used for scene image prompts and newsletter drafting.',
        // Only models that return plain text by default: the pinned backend
        // anthropic SDK (0.39) cannot parse thinking blocks from 5.x models.
        options: [
          { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 (Fast, Cost-effective)' },
          { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Balanced)' },
          { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (Best Quality)' },
        ],
      },
    ],
    setup: {
      steps: [
        'Sign up at console.anthropic.com',
        'Go to Settings → API Keys',
        'Click "Create Key" and copy it (starts with sk-ant-)',
      ],
      link: 'https://console.anthropic.com/settings/keys',
      linkText: 'Open Anthropic Console',
    },
  },
  {
    id: 'openai',
    name: 'OpenAI Text-to-Speech',
    shortName: 'OpenAI',
    description: 'Voice responses for the Claude Code widget',
    icon: Mic,
    accent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    testSlug: 'openai',
    configuredKey: 'openai_configured',
    identifier: (settings) => settings?.openai_api_key_masked
      && `Saved key ${settings.openai_api_key_masked}`,
    note: 'OpenAI TTS is used for voice responses in the Claude Code widget. Cost: ~$0.015 per 1,000 characters.',
    fields: [
      { key: 'openai_api_key', label: 'API key', kind: 'secret', placeholder: 'Enter OpenAI API key (sk-...)' },
      {
        key: 'openai_tts_voice',
        label: 'Default voice',
        kind: 'select',
        defaultValue: 'nova',
        options: [
          { value: 'nova', label: 'Nova - Warm, conversational' },
          { value: 'alloy', label: 'Alloy - Neutral, professional' },
          { value: 'onyx', label: 'Onyx - Deep, authoritative' },
          { value: 'shimmer', label: 'Shimmer - Expressive, energetic' },
          { value: 'echo', label: 'Echo - Soft, calm' },
          { value: 'fable', label: 'Fable - Storytelling quality' },
        ],
      },
      {
        key: 'openai_tts_speed',
        label: 'Speed',
        kind: 'range',
        defaultValue: 1.0,
        min: 0.5,
        max: 2.0,
        step: 0.1,
      },
    ],
    setup: {
      steps: [
        'Sign up or log in at platform.openai.com',
        'Go to API Keys in your account settings',
        'Click "Create new secret key" and copy it (starts with sk-)',
        'Add billing/credits to your account if needed',
      ],
      link: 'https://platform.openai.com/api-keys',
      linkText: 'Open OpenAI API Keys',
    },
  },
  {
    id: 'google_places',
    name: 'Google Places API',
    shortName: 'Google Places',
    description: 'Import reviews from Google',
    icon: MapPin,
    accent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
    testSlug: 'google-places',
    configuredKey: 'google_places_configured',
    identifier: (settings) => settings?.google_places_api_key_masked
      && `Saved key ${settings.google_places_api_key_masked}`,
    fields: [
      { key: 'google_places_api_key', label: 'API key', kind: 'secret', placeholder: 'Enter Google Places API key' },
    ],
    setup: {
      steps: [
        'Go to Google Cloud Console and create/select a project',
        'Enable the "Places API" in APIs & Services → Library',
        'Go to Credentials → Create Credentials → API Key',
        'Restrict the key to Places API for security',
      ],
      link: 'https://console.cloud.google.com/apis/credentials',
      linkText: 'Open Google Cloud Console',
    },
  },
  {
    id: 'google_tts',
    name: 'Google Cloud Text-to-Speech',
    shortName: 'Google TTS',
    description: 'Japanese pronunciation audio for vocabulary',
    icon: Volume2,
    accent: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    testSlug: 'google-tts',
    configuredKey: 'google_tts_configured',
    identifier: (settings) => settings?.google_tts_project_id
      && `Project ${settings.google_tts_project_id}`,
    fields: [
      {
        key: 'google_tts_credentials',
        label: 'Service account credentials (JSON)',
        kind: 'secret-json',
        placeholder: 'Paste your Google Cloud service account JSON here...',
      },
    ],
    setup: {
      steps: [
        'Go to Google Cloud Console and select your project',
        'Enable "Cloud Text-to-Speech API" in APIs & Services',
        'Go to IAM & Admin → Service Accounts',
        'Create a service account, then create a JSON key',
        'Download the JSON file and paste its contents into the credentials field',
      ],
      link: 'https://console.cloud.google.com/iam-admin/serviceaccounts',
      linkText: 'Open Service Accounts',
    },
  },
  {
    id: 'meta',
    name: 'Meta (Facebook/Instagram)',
    shortName: 'Meta',
    description: 'OAuth credentials for social media publishing',
    icon: Facebook,
    accent: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
    testSlug: 'meta',
    configuredKey: 'meta_configured',
    identifier: (settings) => settings?.meta_app_id_masked
      && `App ID ${settings.meta_app_id_masked}`,
    note: 'Used for publishing posts to Facebook Pages and Instagram accounts via the Graph API.',
    fields: [
      { key: 'meta_app_id', label: 'App ID', kind: 'credential', placeholder: 'Enter Meta App ID' },
      { key: 'meta_app_secret', label: 'App secret', kind: 'secret', placeholder: 'Enter Meta App Secret' },
    ],
    setup: {
      steps: [
        'Go to Meta for Developers and create an app',
        'Select "Business" type and configure basic settings',
        'Add Facebook Login and Instagram Graph API products',
        'Copy the App ID and App Secret from Settings → Basic',
        'Add OAuth redirect URL: https://api.owazamartialarts.com/api/admin/social/platforms/callback/facebook',
      ],
      link: 'https://developers.facebook.com/apps/',
      linkText: 'Open Meta Developers',
    },
  },
  {
    id: 'tiktok',
    name: 'TikTok for Business',
    shortName: 'TikTok',
    description: 'OAuth credentials for TikTok video publishing',
    icon: Share2,
    accent: 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300',
    testSlug: 'tiktok',
    configuredKey: 'tiktok_configured',
    identifier: (settings) => settings?.tiktok_client_key_masked
      && `Client key ${settings.tiktok_client_key_masked}`,
    note: 'Used for publishing video content to TikTok via their Content Posting API.',
    fields: [
      { key: 'tiktok_client_key', label: 'Client key', kind: 'credential', placeholder: 'Enter TikTok Client Key' },
      { key: 'tiktok_client_secret', label: 'Client secret', kind: 'secret', placeholder: 'Enter TikTok Client Secret' },
    ],
    setup: {
      steps: [
        'Go to TikTok for Developers and create an app',
        'Select appropriate use case for content publishing',
        'Configure OAuth settings and get approval',
        'Copy the Client Key and Client Secret',
        'Add OAuth redirect URL: https://api.owazamartialarts.com/api/admin/social/platforms/callback/tiktok',
      ],
      link: 'https://developers.tiktok.com/apps/',
      linkText: 'Open TikTok Developers',
    },
  },
];

export const PROVIDER_GROUPS = [
  { id: 'ai', label: 'AI & media', providerIds: ['fal', 'anthropic', 'openai'] },
  { id: 'google', label: 'Google Cloud', providerIds: ['google_places', 'google_tts'] },
  { id: 'social', label: 'Social publishing', providerIds: ['meta', 'tiktok'] },
];

export const PROVIDERS_BY_ID = Object.fromEntries(PROVIDERS.map((provider) => [provider.id, provider]));

export const isProviderId = (value) => Object.hasOwn(PROVIDERS_BY_ID, value || '');

/** Saved (non-secret) values for a provider; write-only fields always start empty. */
export const baselineFor = (provider, settings) => Object.fromEntries(provider.fields.map((field) => {
  if (isWriteOnlyField(field)) return [field.key, ''];
  const saved = settings?.[field.key];
  // Mirrors the legacy `data.x || default` initialisation.
  return [field.key, saved || field.defaultValue];
}));

export const buildBaselines = (settings) => Object.fromEntries(
  PROVIDERS.map((provider) => [provider.id, baselineFor(provider, settings)])
);

export const isDraftDirty = (provider, draft, baseline) => provider.fields.some(
  (field) => draft?.[field.key] !== baseline?.[field.key]
);

/**
 * Client-side checks run before a save. Messages never echo the entered value.
 * Returns `{ [fieldKey]: message, _form?: message }`; empty object when valid.
 */
export const validateDraft = (provider, draft) => {
  const errors = {};
  provider.fields.forEach((field) => {
    if (!isWriteOnlyField(field)) return;
    const value = draft?.[field.key] || '';
    if (!value) return;
    if (field.kind === 'secret-json') {
      let parsed = null;
      try {
        parsed = JSON.parse(value);
      } catch {
        parsed = null;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors[field.key] = 'Paste the complete service account JSON file. The pasted text is not valid JSON.';
      }
      return;
    }
    if (!value.trim()) {
      errors[field.key] = `${field.label} cannot be blank.`;
    } else if (value !== value.trim()) {
      errors[field.key] = `Remove the spaces or line breaks before or after the ${field.label.toLowerCase()}.`;
    }
  });

  const hasSettingFields = provider.fields.some((field) => !isWriteOnlyField(field));
  const hasWriteOnlyValue = provider.fields.some(
    (field) => isWriteOnlyField(field) && draft?.[field.key]
  );
  if (!hasSettingFields && !hasWriteOnlyValue) {
    errors._form = 'Enter a new value before saving.';
  }
  return errors;
};

/** The PUT body for one provider — identical in shape to the legacy per-section payloads. */
export const buildUpdatePayload = (provider, draft) => {
  const updates = {};
  provider.fields.forEach((field) => {
    const value = draft?.[field.key];
    if (isWriteOnlyField(field)) {
      if (value) updates[field.key] = value;
    } else {
      updates[field.key] = value;
    }
  });
  return updates;
};
